import React, { useRef, useCallback } from "react"
import { useOutletContext, useParams } from "react-router-dom"
import { Space, Typography } from "antd"

import { W1, W2 } from "@components"
import { InvestmentDrawer } from "@components/Investment"
import {
  useSchedulesStore,
  DistributionsTable,
  useSendNotifications,
  DistributionsImportModal,
  DistributionsScheduleModal,
  DistributionsScheduleSummary,
} from "@components/Distributions"
import {
  TransactionDrawer,
  TransactionCreateModal,
} from "@modules/backstage/transactions"

import DistributionBankAccount from "./DistributionBankAccount"


const ProjectDistributionsPage = () => {
  const createModal = useRef({})
  const investmentDrawer = useRef({})
  const transactionDrawer = useRef({})
  const distributionsImportModal = useRef({})
  const distributionsScheduleModal = useRef({})

  const {
    isLoading: isSchedulesLoading,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    getProjectSchedule,
    enableSchedule,
    disableSchedule,
  } = useSchedulesStore();

  const { id: projectId } = useParams()
  const sendNotifications = useSendNotifications()
  const { table, getProject, activeInvestments = [], bankAccountsMap } = useOutletContext()

  const openCreateModal = () =>
    createModal.current.open()

  const openImportModal = () =>
    distributionsImportModal.current.open()

  const openScheduleModal = () =>
    distributionsScheduleModal.current.open()

  const openTransactionDrawer = useCallback(
    distribution => transactionDrawer.current.open(distribution)
  , [])

  const openInvestmentDrawer = useCallback(
    investmentId => investmentDrawer.current.open(investmentId)
  , [])

  const indexTransactions = showSpinner =>
    table.current.indexItems(showSpinner)

  const onCreateSuccess = async () => {
    await table.current.indexItems(false)
    await sendNotifications()
  }

  const onImportSuccess = async (isRegisterOperation) => {
    await table.current.indexItems(false)

    if (!isRegisterOperation) {
      await sendNotifications()
    }
  }

  const project = getProject(projectId)
  const { distributionBankAccountId } = project

  const bankAccount = bankAccountsMap[distributionBankAccountId]
  const isInvalidBankAccount = !bankAccount || bankAccount.isInvalid

  const hasDistributionsAccount = !!project && !!distributionBankAccountId
  
  const nextDate = getProjectSchedule(projectId)?.nextDate
  const scheduleStatus = getProjectSchedule(projectId)?.status
  const totalAmount = getProjectSchedule(projectId)?.totalAmount

  return (
    <div style={{ padding: W1 }}>
      <Space size={W2}>
        <DistributionBankAccount />
        <Typography.Paragraph>
          <DistributionsScheduleSummary 
            nextDate={nextDate} 
            totalAmount={totalAmount} 
            status={scheduleStatus}
          />
        </Typography.Paragraph>
      </Space>

      <DistributionsTable
        table={table}
        projectId={projectId}
        openCreateModal={openCreateModal}
        openImportModal={openImportModal}
        openScheduleModal={openScheduleModal}
        openInvestmentDrawer={openInvestmentDrawer}
        isInvalidBankAccount={isInvalidBankAccount}
        isSchedulesLoading={isSchedulesLoading}
        openTransactionDrawer={openTransactionDrawer}
        hasDistributionsAccount={hasDistributionsAccount}
      />

      <TransactionCreateModal
        modal={createModal}
        onSuccess={onCreateSuccess}
        investments={activeInvestments}
      />

      <InvestmentDrawer
        drawer={investmentDrawer}
      />

      <TransactionDrawer
        drawer={transactionDrawer}
        indexTransactions={indexTransactions}
      />

      <DistributionsImportModal
        modal={distributionsImportModal}
        onSuccess={onImportSuccess}
        investments={activeInvestments}
        isInvalidBankAccount={isInvalidBankAccount}
        hasDistributionsAccount={hasDistributionsAccount}
      />

      <DistributionsScheduleModal
        modal={distributionsScheduleModal}
        schedule={getProjectSchedule(projectId)}
        investments={activeInvestments}
        createSchedule={createSchedule}
        updateSchedule={updateSchedule}
        deleteSchedule={deleteSchedule}
        enableSchedule={enableSchedule}
        disableSchedule={disableSchedule}
      />
    </div>
  )
}

export default ProjectDistributionsPage
